import { useCallback } from 'react'

import { useSoapWrite } from '@/apps/latest/features/yakureki/hooks/useSoapWrite'
import { useToast } from '@/shared/contexts/ToastContext'
import { type YakurekiDetail } from '@/shared/types/yakurekiCore'
import {
  type SoapContentItem,
  type SoapSectionKeys,
} from '@/shared/types/yakurekiSoap'

import { useLineEditing } from './useLineEditing'

interface Args {
  event: YakurekiDetail
  reloadEvent: () => Promise<void>
}

// SOAP 行の inline 編集 / 新規行追加。状態機械は共通の useLineEditing に集約し、ここでは
// 「SOAP の items 解決」と「保存 (useSoapWrite + reloadEvent + 成功トースト)」の
// 差分だけを注入する。連続追加・往復中の割り込みの扱いは useLineEditing 側のコメント参照。
//
// items 解決も保存 body も cache の最新から組む (props の閉包から組むと、往復中に別の
// section を保存した 2 本目が 1 本目の変更を落とす。理由は useSoapWrite のコメント)。
export const useSoapLineEditing = ({ event, reloadEvent }: Args) => {
  const { showToast } = useToast()
  const { write, currentSoaps } = useSoapWrite({ eventId: event.id })

  const itemsOf = useCallback(
    (key: SoapSectionKeys): SoapContentItem[] =>
      ((currentSoaps() ?? event.soaps)?.[key] ?? []) as SoapContentItem[],
    [currentSoaps, event.soaps]
  )

  // key の items を差し替えた soaps blob を保存する。成功で true、成功トーストもここで出す。
  const commit = useCallback(
    async (key: SoapSectionKeys, nextItems: SoapContentItem[]) => {
      const ok = await write((current) => {
        const { id: _id, ...rest } = current
        return { ...rest, [key]: nextItems }
      }, 'manual')
      if (!ok) {
        showToast('保存に失敗しました', 'error')
        return false
      }
      await reloadEvent()
      showToast('SOAP を保存しました', 'success')
      return true
    },
    [write, reloadEvent, showToast]
  )

  const line = useLineEditing<SoapSectionKeys>({ itemsOf, commit })

  // checkbox (採否 = is_selected) トグル。訪問報告書 useHomeVisitReportEditing.toggleSelect と
  // 対称。is_selected は content 差分を伴わないため back は新バージョン/編集履歴を作らず既存
  // レコードを in-place 更新する (yakureki/crud.py update_soap_with_history)。行編集の成功
  // トーストは出さず (毎クリックで煩いため)、失敗時のみ通知する。
  //
  // トグル対象の items も recipe の中で cache から取る: 連打したとき 2 回目が render 時点の
  // 値を見ると、1 回目のトグルが無かったことになって元に戻る。
  const toggleSelect = useCallback(
    (key: SoapSectionKeys, index: number) => {
      // 対象行が無いときは無反応 (= 保存失敗ではないので通知しない)。
      if (!itemsOf(key)[index]) return
      void (async () => {
        const ok = await write((current) => {
          const items = [...((current[key] ?? []) as SoapContentItem[])]
          const item = items[index]
          if (!item) return null
          items[index] = { ...item, is_selected: !(item.is_selected ?? true) }
          const { id: _id, ...rest } = current
          return { ...rest, [key]: items }
        }, 'manual')
        if (ok) {
          await reloadEvent()
        } else {
          showToast('保存に失敗しました', 'error')
        }
      })()
    },
    [itemsOf, write, reloadEvent, showToast]
  )

  return {
    ...line,
    toggleSelect,
  }
}

export type SoapLineEditing = ReturnType<typeof useSoapLineEditing>
